<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div class="d-flex align-items-center">
          <router-link :to="{ name: 'siteManagement' }" class="btn btn-sm btn-outline-secondary mr-3">
            <i class="fas fa-arrow-left mr-1"></i> Back
          </router-link>
          <div>
            <p class="text-uppercase text-muted mb-0 super-page-kicker">Site Detail</p>
            <h2 class="h3 mb-0">{{ siteInfo.name || 'Site Detail' }}</h2>
          </div>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">

      <!-- Site summary card -->
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-center">
            <div class="col-sm-4 col-lg-3 mb-3 mb-sm-0">
              <p class="super-page-kicker mb-1">Site Name</p>
              <h4 class="mb-0">{{ siteInfo.name || '—' }}</h4>
            </div>
            <div class="col-sm-4 col-lg-3 mb-3 mb-sm-0" v-if="serviceInfo">
              <p class="super-page-kicker mb-1">Meter Type</p>
              <h4 class="mb-0">{{ serviceInfo.service_name }}</h4>
            </div>
            <div class="col-sm-4 col-lg-3 mb-3 mb-sm-0">
              <p class="super-page-kicker mb-1">Total Lots</p>
              <h4 class="mb-0">{{ siteDetails.data ? siteDetails.data.total : '—' }}</h4>
            </div>
            <div class="col-lg-3 d-flex justify-content-lg-end mt-3 mt-lg-0">
              <b-button variant="primary" class="mr-2" @click="importFile">
                <i class="fas fa-upload mr-1"></i> Import Lots
              </b-button>
              <a :href="exportUrl" target="_blank" class="btn btn-outline-primary">
                <i class="fas fa-download mr-1"></i> Export Lots
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Import lots (toggleable) -->
      <div v-if="importFlag" class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <p class="super-page-kicker mb-1">Lots Import</p>
              <h4 class="mb-1">Upload CSV</h4>
              <p class="text-muted mb-0 small">
                Include <strong>Quote Part (%)</strong> in the CSV when available. It is used as the fallback basis for bowser allocation if historical common consumption is not available.
              </p>
            </div>
            <b-link class="text-muted" @click="importFile">
              <i class="fas fa-times"></i>
            </b-link>
          </div>
          <div class="row align-items-end">
            <div class="col-lg-7 mb-3 mb-lg-0">
              <b-form-file accept=".csv" v-model="uploadFile" placeholder="Choose a CSV file..."></b-form-file>
            </div>
            <div class="col-lg-5 d-flex align-items-center">
              <b-button variant="success" :disabled="!uploadFile" @click="fileImport(uploadFile)" class="mr-3">
                <i class="fas fa-upload mr-1"></i> Upload
              </b-button>
              <b-link :href="siteCsvDump + 'site-csv-dump'" target="_blank" class="text-muted small">
                <i class="fas fa-file-csv mr-1"></i> Download sample file
              </b-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Quote part card — water only, collapsible -->
      <div v-if="isWaterService" class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start"
            :class="{ 'mb-4': quotePartVisible }">
            <div class="mb-3 mb-lg-0">
              <p class="super-page-kicker mb-1">Configuration</p>
              <h4 class="mb-1">Quote-part maintenance</h4>
              <p class="text-muted mb-0 small">
                Download the site-scoped template, update only <strong>Quote Part (%)</strong>, and upload the CSV back here.
              </p>
            </div>
            <b-button variant="outline-secondary" size="sm" class="mt-2 mt-lg-0" @click="quotePartVisible = !quotePartVisible">
              <i class="fas mr-1" :class="quotePartVisible ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              {{ quotePartVisible ? 'Collapse' : 'Expand' }}
            </b-button>
          </div>

          <div v-if="quotePartVisible">
            <div v-if="quotePartResultMessage" class="alert alert-info border-0 mb-3">
              {{ quotePartResultMessage }}
            </div>

            <div class="row align-items-end mb-4">
              <div class="col-lg-8">
                <p class="text-muted small mb-0">
                  Download the template pre-filled with barcodes for this site, edit the Quote Part (%) column, then upload it back.
                </p>
              </div>
              <div class="col-lg-4 d-flex justify-content-lg-end mt-3 mt-lg-0">
                <b-button variant="outline-primary" :disabled="quotePartTemplateLoading" @click="downloadQuotePartTemplate">
                  <i class="fas fa-download mr-1"></i>
                  {{ quotePartTemplateLoading ? 'Preparing...' : 'Download Template' }}
                </b-button>
              </div>
            </div>

            <div class="row align-items-end">
              <div class="col-lg-7 mb-3 mb-lg-0">
                <label class="form-control-label small font-weight-bold">Quote-part CSV</label>
                <b-form-file accept=".csv" v-model="quotePartFile" placeholder="Choose a CSV file..."></b-form-file>
                <small class="d-block mt-2 text-muted">
                  Use headers <strong>Barcode</strong> and <strong>Quote Part (%)</strong>.
                </small>
              </div>
              <div class="col-lg-5 d-flex align-items-center">
                <b-button variant="success" class="mr-3" :disabled="!quotePartFile || quotePartUploadLoading"
                  @click="uploadQuoteParts">
                  <i class="fas fa-upload mr-1"></i>
                  {{ quotePartUploadLoading ? 'Uploading...' : 'Upload Quote Parts' }}
                </b-button>
                <b-button variant="link" class="px-0 text-muted" :disabled="quotePartUploadLoading"
                  @click="clearQuotePartUpload">
                  Clear
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Lots table -->
      <div class="card shadow-sm border-0 mb-5">
        <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <h3 class="mb-0">Lots</h3>
          <span v-if="lotsList.total" class="badge badge-pill badge-primary">{{ lotsList.total }} total</span>
        </div>

        <div v-if="loading" class="text-center py-6">
          <b-spinner variant="primary" class="mb-3"></b-spinner>
          <p class="text-muted mb-0">Loading lots...</p>
        </div>

        <div v-else class="table-responsive">
          <b-table class="mb-0" striped hover :items="lotsList.data" :fields="fields">
            <template slot="index" slot-scope="data">
              {{ data.index + lotsList.from }}
            </template>
            <template slot="lot_no" slot-scope="data">
              {{ data.value }}
            </template>
            <template slot="ownerName" slot-scope="data">
              {{ data.item.lot_owner ? data.item.lot_owner.name : '—' }}
            </template>
            <template slot="meterId" slot-scope="data">
              {{ data.item.barcode ? data.item.barcode.meter_id : '—' }}
            </template>
            <template slot="barcode" slot-scope="data">
              {{ data.item.barcode ? data.item.barcode.barcode : '—' }}
            </template>
          </b-table>
        </div>

        <div class="card-footer border-0 bg-white" v-if="lotsList.total > 10">
          <b-pagination align="right" @input="pageClick(currentPage)" :total-rows="lotsList.total"
            v-model="currentPage" :per-page="10">
          </b-pagination>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import userData from '@/pages/Common/userData';
import { apiUrl } from '@/config'
export default {
  components: {
    userData
  },
  data() {
    return {
      currentPage: 1,
      totalPageCount: 1,
      loading: false,
      siteCsvDump: apiUrl,
      exportUrl: '',
      service_provider_id: this.$route.params.serviceproviderid,
      site_id: this.$route.params.siteid,
      service_type_id: this.$route.params.servicetypeid,
      importFlag: false,
      importData: "",
      siteInfo: '',
      serviceInfo: '',
      uploadFile: '',
      quotePartFile: null,
      quotePartVisible: false,
      quotePartTemplateLoading: false,
      quotePartUploadLoading: false,
      quotePartResultMessage: '',
      lotsList: [],
      fields: [
        { key: 'index',     label: 'S.No',       sortable: false, class: 'text-center' },
        { key: 'lot_no',    label: 'Lot No',      sortable: true,  class: 'text-center' },
        { key: 'ownerName', label: 'Owner Name',  sortable: true,  class: 'text-center' },
        { key: 'meterId',   label: 'Meter ID',    sortable: false, class: 'text-center' },
        { key: 'barcode',   label: 'Barcode',     sortable: false, class: 'text-center' }
      ],
    }
  },
  computed: {
    ...mapGetters('siteManagement', ['siteDetails', 'sampleFile']),
    normalizedServiceTypeId() {
      return this.service_type_id || 1;
    },
    isWaterService() {
      if (this.serviceInfo && this.serviceInfo.service_name) {
        return this.serviceInfo.service_name.toLowerCase().includes('water');
      }
      return Number(this.service_type_id) === 1;
    },
  },
  methods: {
    ...mapActions(['FILE_UPLOAD']),
    ...mapActions('siteManagement', ['SITE_DETAILS', 'SITE_QUOTE_PART_TEMPLATE', 'SITE_QUOTE_PART_UPLOAD']),
    getSiteSiteDetail() {
      this.loading = true
      const data = {
        service_provider_id: this.service_provider_id,
        service_type_id: this.normalizedServiceTypeId,
        site_id: this.site_id,
        page: this.currentPage
      }
      this.exportUrl = `${apiUrl}site-csv?service_provider_id=${this.service_provider_id}&site_id=${this.site_id}&service_type_id=${this.normalizedServiceTypeId}`
      this.SITE_DETAILS(data).then(() => {
        this.siteInfo = this.siteDetails.site_name
        this.serviceInfo = this.siteDetails.service
        this.lotsList = this.siteDetails.data
      }).catch(() => {
      }).finally(() => {
        this.loading = false
      })
    },
    pageClick(data) {
      this.currentPage = data
      this.getSiteSiteDetail();
    },
    importFile() {
      this.importFlag = !this.importFlag
      this.uploadFile = ''
    },
    fileImport(data) {
      this.loading = true;
      const formData = new FormData()
      formData.append('site_id', this.site_id)
      formData.append('service_type_id', this.normalizedServiceTypeId)
      formData.append('service_provider_id', this.service_provider_id);
      formData.append('file', data);
      this.FILE_UPLOAD(formData).then((response) => {
        this.loading = false;
        if (response.status) {
          this.SUCCESS_TOAST_MSG('File uploaded successfully')
          this.importFlag = false
          this.uploadFile = ''
          this.getSiteSiteDetail()
        }
      }).catch(() => { })
    },
    clearQuotePartUpload() {
      this.quotePartFile = null;
      this.quotePartResultMessage = '';
    },
    downloadQuotePartTemplate() {
      this.quotePartTemplateLoading = true;
      const request = {
        site_id: this.site_id,
        service_type_id: this.normalizedServiceTypeId
      };
      this.SITE_QUOTE_PART_TEMPLATE(request)
        .then((response) => {
          const csvContent = typeof response === 'string' ? response : '';
          if (!csvContent) {
            this.ERROR_TOAST_MSG('Unable to download quote-part template.');
            return;
          }
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          const siteName = this.siteInfo && this.siteInfo.name ? this.siteInfo.name : 'site';
          const safeSiteName = siteName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          link.href = downloadUrl;
          link.setAttribute('download', `${safeSiteName || 'site'}-quote-part-template.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        })
        .catch(() => {
          this.ERROR_TOAST_MSG('Unable to download quote-part template.');
        })
        .finally(() => {
          this.quotePartTemplateLoading = false;
        });
    },
    uploadQuoteParts() {
      if (!this.quotePartFile) {
        this.ERROR_TOAST_MSG('Select a quote-part CSV file.');
        return;
      }
      const formData = new FormData();
      formData.append('site_id', this.site_id);
      formData.append('service_type_id', this.normalizedServiceTypeId);
      formData.append('file', this.quotePartFile);
      this.quotePartUploadLoading = true;
      this.SITE_QUOTE_PART_UPLOAD(formData)
        .then((response) => {
          this.quotePartResultMessage = response.message || 'Quote-part CSV uploaded successfully.';
          this.quotePartFile = null;
          this.getSiteSiteDetail();
        })
        .catch((error) => {
          this.quotePartResultMessage = error && error.message
            ? error.message
            : 'Quote-part upload failed.';
        })
        .finally(() => {
          this.quotePartUploadLoading = false;
        });
    }
  },
  created() {
    this.getSiteSiteDetail(1);
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5e72e4;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/siteDetailView.vue